import { useState, useEffect } from 'react';
import { Text, Dropdown, IDropdownOption, IDropdownStyles, Spinner, Stack } from '@fluentui/react';
import { getEmptyGuid } from './Utils';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { UserRegistrationPeriod, WorkType } from '../api/generated/data-contracts';
import { useTranslation } from 'react-i18next';

const dropdownStyles: Partial<IDropdownStyles> = {
    root: {
        height: '100%',
        width: '100%',
        '&:hover': {
            backgroundColor: 'var(--semanticColors-inputBackground)',
        },
    },
    dropdown: { height: '100%', width: '100%' },
    title: {
        maxWidth: '100%',
        borderWidth: 0,
        borderStyle: 'none',
        backgroundColor: 'none',
    },
};

export const titleStyles: any = {
    fontSize: 13,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    overflowWrap: 'break-word',
    textAlign: 'center',
};

const onRenderTitle = (options: IDropdownOption[]): JSX.Element => {
    const option = options[0];
    return (
        <div className="tag_dropdown_item">
            <Text styles={{ root: titleStyles }}>{option.text}</Text>
        </div>
    );
};

interface workTypePickerProps {
    row: UserRegistrationPeriod;
    existingTaskTimeRows: UserRegistrationPeriod[];
    readOnly: boolean;
    onChange: (row: UserRegistrationPeriod, option: WorkType, changeCompleted: Function) => void;
}

export const WorkTypePicker = (props: workTypePickerProps) => {
    const { t } = useTranslation('timesheet');
    const [dropdownOptions, setDropdownOptions] = useState<IDropdownOption[]>();
    const [hidden, setHidden] = useState<boolean>(false);
    const [changing, setChanging] = useState<boolean>(false);
    const {
        initialData: { allowedWorkTypes: allowedWorkTypesConfig, projectTypes },
    } = useConfigValuesState();
    useEffect(() => {
        const userWorkTypes = [...allowedWorkTypesConfig];
        const projectWorkTypes = projectTypes.find(_ => _.id === props.row.projectWorkType?.id)?.allowedWorkTypes || [];

        //Hide if projectworktype is only empty guid
        if (projectWorkTypes.length === 1 && projectWorkTypes[0].id === getEmptyGuid()) {
            setDropdownOptions([]);
            setHidden(true);
            return;
        }

        const userWorkTypesInProject = userWorkTypes.filter(uw => projectWorkTypes.some(pw => pw.id === uw.id));
        const projectWorkTypesInUser = projectWorkTypes.filter(uw => userWorkTypes.some(pw => pw.id === uw.id));

        //Take worktypes that are in both lists
        const availableWorkTypes = [...projectWorkTypesInUser, ...userWorkTypesInProject].filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);

        const rowWorkType = props.row.workType;

        const existingTaskTimeRows = props.existingTaskTimeRows.filter(_ => _.taskId === props.row.taskId);
        const allowedWorkTypes = availableWorkTypes?.filter(
            // workType => !existingTaskTimeRows.filter(task => task.key !== props.row.key).some(t => t.workType?.id === workType.id),
            workType => !existingTaskTimeRows.filter(task => task.assignmentId !== props.row.assignmentId).some(t => t.workType?.id === workType.id),
        );
        const allowedWorkTypesDisabledCurrent = allowedWorkTypes?.map(aw => ({ ...aw, disabled: aw.id === rowWorkType?.id }));

        //If assignment is closed, make worktype readonly
        if (props.row.taskClosed === true) {
            const options = rowWorkType
                ? [{ id: rowWorkType.id, name: `(${rowWorkType.name})`, disabled: true }]
                : [{ id: getEmptyGuid(), name: t('worktype.noSelection'), disabled: true }];
            setDropdownOptions(options?.map(workType => ({ key: workType.id, text: workType.name, disabled: workType.disabled })) || []);
            return;
        }

        const nullWorkType = rowWorkType == null;
        const workTypeAllowed = availableWorkTypes?.find(wt => wt.id === rowWorkType?.id);

        //This should never happen after empty guid worktype?
        if (nullWorkType) {
            //If the row has null in worktype, we create a option with emptyguid
            allowedWorkTypesDisabledCurrent.push({ id: getEmptyGuid(), name: t('worktype.noSelection'), disabled: true });
        } else if (!workTypeAllowed) {
            //If work type is not in userworktype, we will show the value, but disabed
            allowedWorkTypesDisabledCurrent.push({ id: rowWorkType.id, name: `(${rowWorkType.name})`, disabled: true });
        }

        setDropdownOptions(allowedWorkTypesDisabledCurrent?.map(workType => ({ key: workType.id, text: workType.name, disabled: workType.disabled })) || []);
    }, [props.existingTaskTimeRows, t]);

    const changeCompleted = () => {
        setChanging(false);
    };

    if (!dropdownOptions || !dropdownOptions?.length) return <></>;

    return (
        <>
            {changing ? (
                <Stack horizontalAlign="center" style={{ width: '100%' }}>
                    <Spinner />
                </Stack>
            ) : (
                <Dropdown
                    onRenderTitle={onRenderTitle}
                    hidden={hidden}
                    selectedKey={props.row.workType?.id || getEmptyGuid()}
                    placeholder={t('worktype.select')}
                    options={dropdownOptions}
                    styles={dropdownStyles}
                    disabled={changing || props.readOnly || (dropdownOptions && dropdownOptions.length <= 1)}
                    onChange={(e, option: any, i) => {
                        setChanging(true);
                        props.onChange(props.row, { id: option.key, name: option.text, userTypes: null }, changeCompleted);
                    }}
                />
            )}
        </>
    );
};
